import { lazy } from 'react';
import { Outlet } from 'react-router-dom';
import PlatformAdminGuard from 'utils/route-guard/PlatformAdminGuard';

// project import
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';

const PlatformHomePage = Loadable(lazy(() => import('pages/home/home')));
const CompanyNewPage = Loadable(lazy(() => import('pages/company/new')));
const CompanyListPage = Loadable(lazy(() => import('pages/company/list')));
const UserNewPage = Loadable(lazy(() => import('pages/user/new')));
const UserListPage = Loadable(lazy(() => import('pages/user/list')));
const PentestDraftPage = Loadable(lazy(() => import('pages/pentest/draft')));
const PentestListPage = Loadable(lazy(() => import('pages/pentest/list')));
const PentestPage = Loadable(lazy(() => import('pages/pentest')));
const PentestTabDetails = Loadable(lazy(() => import('pages/pentest/TabDetails')));
const PentestTabFindings = Loadable(lazy(() => import('pages/pentest/TabFindings')));
const PentestTabEdit = Loadable(lazy(() => import('pages/pentest/TabEdit')));
const CreditsPage = Loadable(lazy(() => import('pages/credits')));
const CreditsSuccessPage = Loadable(lazy(() => import('pages/credits/success')));
const PentesterListPage = Loadable(lazy(() => import('pages/pentester/list')));
const PentesterNewPage = Loadable(lazy(() => import('pages/pentester/new')));
const FindingEditPage = Loadable(lazy(() => import('pages/finding/edit')));
const FindingViewPage = Loadable(lazy(() => import('pages/finding/view')));
const AdministratorListPage = Loadable(lazy(() => import('pages/administrator/list')));
const AdministratorNewPage = Loadable(lazy(() => import('pages/administrator/new')));
const PentesterPage = Loadable(lazy(() => import('pages/pentester')));
const ProfilePage = Loadable(lazy(() => import('pages/profile')));
const FilePage = Loadable(lazy(() => import('pages/file')));
const TemplateListPage = Loadable(lazy(() => import('pages/template/list')));
const TemplateNewPage = Loadable(lazy(() => import('pages/template/new')));
const TemplatePreviewPage = Loadable(lazy(() => import('pages/template/preview')));

const CompanySettingsPage = Loadable(lazy(() => import('pages/company-users/CompanySettings')));
const CompanyAccessListPage = Loadable(lazy(() => import('pages/company-users/CompanyAccessList')));

const SessionsPage = Loadable(lazy(() => import('pages/security/Sessions')));
const AuditEventsPage = Loadable(lazy(() => import('pages/security/AuditEvents')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = [
  {
    path: '',
    element: <DashboardLayout />,
    children: [
      {
        path: '',
        element: <PlatformHomePage />,
      },
      {
        path: 'Profile',
        element: <ProfilePage />,
      },
      {
        path: 'profile/sessions',
        element: <SessionsPage />,
      },
      {
        path: 'credits',
        children: [
          {
            path: '',
            element: <CreditsPage />,
          },
          {
            path: 'success',
            element: <CreditsSuccessPage />,
          },
        ],
      },
      {
        path: 'company',
        children: [
          { path: 'settings', element: <CompanySettingsPage /> },
          {
            path: '',
            element: (
              <PlatformAdminGuard>
                <CompanyListPage />
              </PlatformAdminGuard>
            ),
          },
          {
            path: 'new',
            element: <CompanyNewPage />,
          },
          {
            path: ':id',
            element: <CompanyNewPage />,
          },
        ],
      },
      {
        path: 'user',
        children: [
          {
            path: '',
            element: <UserListPage />,
          },
          {
            path: 'new',
            element: <UserNewPage />,
          },
        ],
      },
      {
        path: 'pentester',
        element: (
          <PlatformAdminGuard>
            <Outlet />
          </PlatformAdminGuard>
        ),
        children: [
          {
            path: '',
            element: <PentesterListPage />,
          },
          {
            path: 'new',
            element: <PentesterNewPage />,
          },
          {
            path: ':id',
            element: <PentesterPage />,
          },
        ],
      },
      {
        path: 'admin',
        element: (
          <PlatformAdminGuard>
            <Outlet />
          </PlatformAdminGuard>
        ),
        children: [
          { path: 'sessions', element: <SessionsPage admin /> },
          { path: 'companies/access-status', element: <CompanyAccessListPage /> },
          { path: 'audit-events', element: <AuditEventsPage /> },
          {
            path: '',
            element: <AdministratorListPage />,
          },
          {
            path: 'new',
            element: <AdministratorNewPage />,
          },
        ],
      },
      {
        path: 'pentest',
        children: [
          {
            path: '',
            element: <PentestListPage />,
          },
          {
            path: 'draft',
            element: <PentestDraftPage />,
          },
          {
            path: 'draft/:id',
            element: <PentestDraftPage />,
          },
          {
            path: ':id',
            element: <PentestPage />,
            children: [
              {
                path: 'details',
                element: <PentestTabDetails />,
              },
              {
                path: 'findings',
                element: <PentestTabFindings />,
              },
              {
                path: 'edit',
                element: <PentestTabEdit />,
              },
              {
                path: 'finding',
                children: [
                  {
                    path: '',
                    element: <FindingEditPage />,
                  },
                  {
                    path: ':findingId',
                    element: <FindingViewPage />,
                  },
                  {
                    path: ':findingId/edit',
                    element: <FindingEditPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'template',
        element: (
          <PlatformAdminGuard>
            <Outlet />
          </PlatformAdminGuard>
        ),
        children: [
          {
            path: '',
            element: <TemplateListPage />,
          },
          {
            path: 'new',
            element: <TemplateNewPage />,
          },
          {
            path: ':id',
            element: <TemplateNewPage />,
          },
          {
            path: ':id/preview',
            element: <TemplatePreviewPage />,
          },
        ],
      },
    ],
  },
  {
    path: 'file/:id',
    element: <FilePage />,
  },
];

export default MainRoutes;
